import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import Header from './Header';
import Footer from './Footer';
import ProductSidebar from './ProductSidebar';
import { useUser } from '../contexts/UserContext';
import './AutoJobApplyLanding.css';

const HERO_IMG = '/images/auto-apply/hero-agent-swarm.png';
const TAILOR_IMG = '/images/auto-apply/resume-tailoring.png';
const SCANNER_IMG = '/images/auto-apply/job-board-scanner.png';
const LIVE_IMG = '/images/auto-apply/live-applications.png';

/* ---------- Three.js orbital agent canvas ---------- */
const AgentSwarmCanvas = () => {
  const mountRef = useRef(null);
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let width = mount.clientWidth;
    let height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 80;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const sprite = (() => {
      const c = document.createElement('canvas');
      c.width = c.height = 64;
      const ctx = c.getContext('2d');
      const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.35, 'rgba(0,255,255,0.9)');
      g.addColorStop(0.75, 'rgba(111,255,209,0.4)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 64, 64);
      return new THREE.CanvasTexture(c);
    })();

    // Orbital application "windows" — 3 rings of points
    const ringSpecs = [
      { count: 12, radius: 26, speed: 0.3, tilt: 0.0 },
      { count: 18, radius: 38, speed: -0.22, tilt: 0.4 },
      { count: 24, radius: 50, speed: 0.16, tilt: -0.3 },
    ];

    const ringObjects = ringSpecs.map((spec) => {
      const positions = new Float32Array(spec.count * 3);
      const angles = new Float32Array(spec.count);
      for (let i = 0; i < spec.count; i++) {
        angles[i] = (i / spec.count) * Math.PI * 2;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        size: 3.2,
        map: sprite,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        color: 0xffffff,
      });
      const points = new THREE.Points(geo, mat);
      scene.add(points);
      return { spec, geo, mat, angles, points };
    });

    // ambient particle drift
    const fieldCount = 200;
    const fieldPos = new Float32Array(fieldCount * 3);
    const fieldSpeeds = new Float32Array(fieldCount);
    for (let i = 0; i < fieldCount; i++) {
      fieldPos[i * 3] = (Math.random() - 0.5) * 180;
      fieldPos[i * 3 + 1] = (Math.random() - 0.5) * 100;
      fieldPos[i * 3 + 2] = (Math.random() - 0.5) * 70;
      fieldSpeeds[i] = 0.04 + Math.random() * 0.12;
    }
    const fieldGeo = new THREE.BufferGeometry();
    fieldGeo.setAttribute('position', new THREE.BufferAttribute(fieldPos, 3));
    const fieldMat = new THREE.PointsMaterial({
      size: 1.0,
      map: sprite,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      color: 0xffffff,
      opacity: 0.5,
    });
    const field = new THREE.Points(fieldGeo, fieldMat);
    scene.add(field);

    // central agent core (icosahedron + larger wireframe halo)
    const coreGeo = new THREE.IcosahedronGeometry(8, 1);
    const core = new THREE.Mesh(
      coreGeo,
      new THREE.MeshBasicMaterial({ color: 0x00ffff, wireframe: true, transparent: true, opacity: 0.5 }),
    );
    scene.add(core);
    const halo = new THREE.Mesh(
      new THREE.IcosahedronGeometry(12, 1),
      new THREE.MeshBasicMaterial({ color: 0x6fffd1, wireframe: true, transparent: true, opacity: 0.25 }),
    );
    scene.add(halo);

    let raf;
    let mx = 0, my = 0, t = 0;
    const onMove = (e) => {
      const r = mount.getBoundingClientRect();
      mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      my = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);
    const onResize = () => {
      width = mount.clientWidth;
      height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', onResize);

    const animate = () => {
      t += 0.01;
      ringObjects.forEach(({ spec, geo, angles }) => {
        const pos = geo.attributes.position.array;
        for (let i = 0; i < spec.count; i++) {
          const a = angles[i] + t * spec.speed;
          const x = Math.cos(a) * spec.radius;
          const y = Math.sin(a) * spec.radius * Math.cos(spec.tilt);
          const z = Math.sin(a) * spec.radius * Math.sin(spec.tilt);
          pos[i * 3] = x;
          pos[i * 3 + 1] = y;
          pos[i * 3 + 2] = z;
        }
        geo.attributes.position.needsUpdate = true;
      });

      const fp = fieldGeo.attributes.position.array;
      for (let i = 0; i < fieldCount; i++) {
        fp[i * 3 + 1] += fieldSpeeds[i] * 0.5;
        if (fp[i * 3 + 1] > 50) fp[i * 3 + 1] = -50;
      }
      fieldGeo.attributes.position.needsUpdate = true;

      core.rotation.x += 0.005;
      core.rotation.y += 0.006;
      halo.rotation.x -= 0.003;
      halo.rotation.y -= 0.004;

      camera.position.x += (mx * 6 - camera.position.x) * 0.04;
      camera.position.y += (-my * 4 - camera.position.y) * 0.04;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', onResize);
      ringObjects.forEach(({ geo, mat }) => { geo.dispose(); mat.dispose(); });
      fieldGeo.dispose();
      fieldMat.dispose();
      coreGeo.dispose();
      core.material.dispose();
      halo.geometry.dispose();
      halo.material.dispose();
      sprite.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="aal-hero-canvas" aria-hidden="true" />;
};

/* ---------- Live applications counter demo ---------- */
const APPLICATION_FEED = [
  { board: 'LinkedIn', role: 'Senior Software Engineer · Stripe', status: 'submitted' },
  { board: 'Indeed', role: 'Backend Engineer · Notion', status: 'submitted' },
  { board: 'Dice', role: 'Full-Stack Developer · Vercel', status: 'submitted' },
  { board: 'Glassdoor', role: 'Platform Engineer · Linear', status: 'submitted' },
  { board: 'LinkedIn', role: 'Staff Engineer · Figma', status: 'submitted' },
  { board: 'Indeed', role: 'Senior SDE · Anthropic', status: 'submitted' },
];

const useCounter = (target, start) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const isFloat = !Number.isInteger(target);
    const dur = 1400;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = target * eased;
      setVal(isFloat ? Math.round(v * 10) / 10 : Math.round(v));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, start]);
  return val;
};

const LiveFeedDemo = () => {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting && revealed === 0) setRevealed(1); }),
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [revealed]);
  useEffect(() => {
    if (revealed === 0 || revealed >= APPLICATION_FEED.length) return;
    const t = setTimeout(() => setRevealed((r) => r + 1), 650);
    return () => clearTimeout(t);
  }, [revealed]);

  const total = useCounter(127, revealed > 0);

  return (
    <section className="aal-section" ref={ref}>
      <div className="aal-section-grid">
        <div className="aal-section-text">
          <span className="aal-eyebrow">LIVE APPLICATION FEED</span>
          <h2 className="aal-h2">
            Watch applications submit <span className="aal-grad-text">in real time</span>.
          </h2>
          <p className="aal-lead">
            Every job the agent applies to drops into your live feed with the board, role,
            company, and status. See exactly where you've applied — and never apply twice.
          </p>
          <ul className="aal-bullet-list">
            <li><span className="aal-dot" />Real-time submission status</li>
            <li><span className="aal-dot" />Per-board breakdown (LinkedIn / Indeed / Dice…)</li>
            <li><span className="aal-dot" />Auto-dedupe so you never double-apply</li>
          </ul>
        </div>

        <div className="aal-feed-card">
          <div className="aal-feed-head">
            <span className="aal-feed-pulse" />
            <span>AGENT RUNNING</span>
            <span className="aal-feed-count">{total} applied today</span>
          </div>
          <div className="aal-feed-list">
            {APPLICATION_FEED.map((row, i) => (
              <div
                key={i}
                className={`aal-feed-row ${i < revealed ? 'lit' : ''}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <span className="aal-feed-board">{row.board}</span>
                <span className="aal-feed-role">{row.role}</span>
                <span className="aal-feed-status">✓</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Process flow ---------- */
const FLOW_STEPS = [
  { title: 'Pick filters', body: 'Roles, locations, salary range, experience level, work auth, remote-only — your hard requirements.' },
  { title: 'Drop your resume', body: 'Or use your saved profile — the agent learns your story once.' },
  { title: 'Pick boards', body: 'LinkedIn, Indeed, Dice, Glassdoor — the agent applies on whichever you authorize.' },
  { title: 'Agent boots a real browser', body: 'Headless Chrome runs in the cloud, signed in to your account, just like a human.' },
  { title: 'Tailored, applied, logged', body: 'Each application is tailored, screening questions answered, submitted, logged.' },
  { title: 'Stop anytime', body: 'One-click stop. Real-time progress. You\'re always in control.' },
];

const FlowSection = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(-1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(0); }),
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (active < 0 || active >= FLOW_STEPS.length - 1) return;
    const t = setTimeout(() => setActive((a) => a + 1), 700);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <section className="aal-section" ref={ref}>
      <div className="aal-section-head">
        <span className="aal-eyebrow">HOW IT WORKS</span>
        <h2 className="aal-h2">
          Six steps. <span className="aal-grad-text">Hundreds of applications.</span>
        </h2>
        <p className="aal-lead aal-center">
          The agent runs in a real browser, on your account, applying like a fast-but-careful you.
        </p>
      </div>

      <div className="aal-flow-grid">
        {FLOW_STEPS.map((s, i) => (
          <div key={s.title} className={`aal-flow-node ${i <= active ? 'lit' : ''}`}>
            <div className="aal-flow-num">{String(i + 1).padStart(2, '0')}</div>
            <h3>{s.title}</h3>
            <p>{s.body}</p>
            {i < FLOW_STEPS.length - 1 && <div className={`aal-flow-link ${i < active ? 'lit' : ''}`} />}
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------- Stats ---------- */
const STATS = [
  { val: 50, suffix: '+', label: 'Applications per session' },
  { val: 4, suffix: '', label: 'Boards supported' },
  { val: 92, suffix: '%', label: 'Submit success rate' },
  { val: 30, suffix: 'min', label: 'Avg. setup-to-running' },
];

const StatTile = ({ stat, start }) => {
  const v = useCounter(stat.val, start);
  return (
    <div className="aal-stat">
      <div className="aal-stat-num">{v}{stat.suffix}</div>
      <div className="aal-stat-lbl">{stat.label}</div>
    </div>
  );
};
const StatsBand = () => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setStart(true); }),
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section className="aal-stats-band" ref={ref}>
      <div className="aal-stats-glow" />
      {STATS.map((s) => <StatTile key={s.label} stat={s} start={start} />)}
    </section>
  );
};

/* ---------- FAQ ---------- */
const FAQS = [
  { q: 'Will I get banned from LinkedIn?', a: 'LinkedIn\'s Terms of Service prohibit automated applications. Terview automates your own account on your behalf, and you choose to use it — but LinkedIn may temporarily restrict or ban your account. You acknowledge this risk before launching. Indeed, Dice, and Glassdoor are friendlier to automation.' },
  { q: 'Does the agent ever submit something I haven\'t reviewed?', a: 'You set the filters and approve the resume up front. Within those bounds, yes — the agent submits autonomously. You can stop it any time, and every submission is logged with the exact role and board.' },
  { q: 'How does the agent handle screening questions?', a: 'Yes/no, work authorization, salary expectation, years of experience — answered automatically from your profile. Free-text fields use your resume + the JD to compose a sentence-or-two response in your voice.' },
  { q: 'Does the agent tailor my resume per job?', a: 'It re-orders sections and emphasises matching keywords for each JD before submission — the underlying content stays yours. No invented experience.' },
  { q: 'Is my login secure?', a: 'Sessions are stored encrypted server-side. The agent runs in an isolated browser profile per user. Credentials never leak between users. You can log out and revoke at any time.' },
  { q: 'Can I run it in the background?', a: 'Yes — the agent runs server-side. You can close your browser tab and the session keeps running. Reload the page to see progress.' },
];

const FaqAccordion = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="aal-section">
      <div className="aal-section-head">
        <span className="aal-eyebrow">QUESTIONS, ANSWERED</span>
        <h2 className="aal-h2">Everything you wanted to ask</h2>
      </div>
      <div className="aal-faq-list">
        {FAQS.map((f, i) => (
          <button
            key={f.q}
            type="button"
            className={`aal-faq-item ${open === i ? 'open' : ''}`}
            onClick={() => setOpen(open === i ? -1 : i)}
          >
            <div className="aal-faq-q">
              <span>{f.q}</span>
              <span className="aal-faq-chev">+</span>
            </div>
            <div className="aal-faq-a"><p>{f.a}</p></div>
          </button>
        ))}
      </div>
    </section>
  );
};

/* ---------- Main page ---------- */
const AutoJobApplyLanding = () => {
  const navigate = useNavigate();
  // Landing page is marketing-only now. The "Launch the agent" CTAs route
  // to the dedicated /auto-apply-session page where the live preferences,
  // queue, and inbox UI live. Keeps the landing fast/scannable instead of
  // making users scroll past a giant embedded app to get to the pitch.
  const goStudio = useCallback(() => {
    navigate('/auto-apply-session');
  }, [navigate]);
  const { user } = useUser();
  const setShowAuthModal = useCallback((show) => {
    if (show) navigate('/?openLogin=1');
  }, [navigate]);

  const heroRef = useRef(null);
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--mx', x);
      el.style.setProperty('--my', y);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div className="aal-page">
      <Header setShowAuthModal={setShowAuthModal} />
      <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />

      <section className="aal-hero" ref={heroRef}>
        <AgentSwarmCanvas />
        <div className="aal-hero-bg" />
        <img src={HERO_IMG} alt="" className="aal-hero-img" loading="eager" />

        <div className="aal-hero-content">
          <span className="aal-hero-pill">
            <span className="aal-pill-dot" /> Real browser · 4 boards · You stay in control
          </span>
          <h1 className="aal-h1">
            Apply to 50 jobs while <span className="aal-grad-text">you sleep</span>.
          </h1>
          <p className="aal-hero-sub">
            A real browser-driven agent applies to LinkedIn, Indeed, Dice, and Glassdoor on your
            behalf — tailoring your resume per job, answering screening questions, logging every
            submission. You set the filters. The agent does the typing.
          </p>

          <div className="aal-hero-cta">
            <button className="aal-btn aal-btn-primary" onClick={goStudio}>
              <span>Launch the agent</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="aal-btn aal-btn-ghost" onClick={() => navigate('/resume-builder')}>
              Build resume first
            </button>
          </div>

          <div className="aal-hero-checks">
            <span>✓ Real browser automation</span>
            <span>✓ Stop any time</span>
            <span>✓ Live submission log</span>
          </div>
        </div>

        {/* Floating glass agent-status card */}
        <div className="aal-floater" aria-hidden="true">
          <div className="aal-floater-card">
            <div className="aal-floater-head">
              <span className="aal-floater-pulse" />
              <span>AGENT · LIVE</span>
            </div>
            <div className="aal-floater-num">23<span>/50</span></div>
            <div className="aal-floater-bar"><div className="aal-floater-bar-fill" /></div>
            <div className="aal-floater-meta">
              <span>LinkedIn ✓ 11</span>
              <span>Indeed ✓ 7</span>
              <span>Dice ✓ 5</span>
            </div>
          </div>
          <div className="aal-floater-glow" />
        </div>
      </section>

{/* The full Auto Apply studio (preferences + queue + inbox) used to live
          embedded here. It now lives at /auto-apply-session — the "Launch the
          agent" CTA above + below routes there directly. Keeps this page
          purely marketing/scannable. */}

      <LiveFeedDemo />

      <section className="aal-section">
        <div className="aal-section-grid aal-flip">
          <div className="aal-img-frame">
            <img src={TAILOR_IMG} alt="Resume tailoring per job" />
            <div className="aal-img-frame-glow" />
          </div>
          <div className="aal-section-text">
            <span className="aal-eyebrow">PER-JOB TAILORING</span>
            <h2 className="aal-h2">
              Every submission is <span className="aal-grad-text">tailored to that JD</span>.
            </h2>
            <p className="aal-lead">
              The agent re-orders your sections and emphasises matching keywords before each
              submission — never invents experience, never lies, never sends the same generic
              resume to two different roles.
            </p>
            <ul className="aal-bullet-list">
              <li><span className="aal-dot" />Keyword density tuned to the JD</li>
              <li><span className="aal-dot" />Section ordering optimized per ATS</li>
              <li><span className="aal-dot" />Truthful — only your real content</li>
            </ul>
          </div>
        </div>
      </section>

      <FlowSection />
      <StatsBand />

      <section className="aal-section">
        <div className="aal-section-grid">
          <div className="aal-section-text">
            <span className="aal-eyebrow">FOUR BOARDS, ONE AGENT</span>
            <h2 className="aal-h2">
              LinkedIn, Indeed, Dice, Glassdoor — <span className="aal-grad-text">all at once</span>.
            </h2>
            <p className="aal-lead">
              The agent boots a real Chrome instance for each board, signs in with your saved
              session, and applies in parallel. Per-board status visible live in the studio.
            </p>
          </div>
          <div className="aal-img-frame">
            <img src={SCANNER_IMG} alt="Multi-board job scanner" />
            <div className="aal-img-frame-glow" />
          </div>
        </div>
      </section>

      <section className="aal-section">
        <div className="aal-section-grid aal-flip">
          <div className="aal-img-frame">
            <img src={LIVE_IMG} alt="Live application feed" />
            <div className="aal-img-frame-glow" />
          </div>
          <div className="aal-section-text">
            <span className="aal-eyebrow">YOU STAY IN CONTROL</span>
            <h2 className="aal-h2">
              Stop anytime. <span className="aal-grad-text">Audit every submission.</span>
            </h2>
            <p className="aal-lead">
              The agent runs server-side — you can close the tab and come back later. Every
              application is logged with role, board, company, and timestamp. One click to stop.
            </p>
          </div>
        </div>
      </section>

      <FaqAccordion />

      <section className="aal-cta-final">
        <div className="aal-cta-card">
          <div className="aal-cta-glow" />
          <h2 className="aal-h2">
            Job-hunting is a <span className="aal-grad-text">numbers game</span>.
          </h2>
          <p>Stop hand-applying one at a time. Launch the agent and play to win.</p>
          <div className="aal-hero-cta center">
            <button className="aal-btn aal-btn-primary" onClick={goStudio}>
              <span>Launch the agent</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="aal-btn aal-btn-ghost" onClick={() => navigate('/resume-builder')}>Build resume first</button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AutoJobApplyLanding;
